/**
 * ResizeObserver错误拦截和处理
 * 用于解决"ResizeObserver loop completed with undelivered notifications"错误
 */

// 全局变量跟踪处理状态 - 已不再使用，保留注释说明
// const observerState = {
//   processing: false,
//   queue: [],
//   throttleTimer: null
// };

/**
 * 设置ResizeObserver错误处理器
 * 拦截并彻底忽略ResizeObserver循环错误
 */
export function setupResizeObserverErrorHandler() {
  // 方法1: 重写console.error以拦截ResizeObserver错误
  const originalConsoleError = console.error;
  console.error = function(...args) {
    if (args[0] && typeof args[0] === 'string' && 
        (args[0].includes('ResizeObserver') || 
         args[0].includes('resize') || 
         args[0].includes('loop'))) {
      // 完全忽略ResizeObserver相关错误
      return;
    }
    originalConsoleError.apply(console, args);
  };
  
  // 方法2: 通过window.onerror捕获
  const originalOnError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    if (message && typeof message === 'string' && message.includes('ResizeObserver')) {
      // 阻止错误传播
      return true;
    }
    if (originalOnError) {
      return originalOnError.apply(this, arguments);
    }
  };
  
  // 方法3: 通过addEventListener捕获
  window.addEventListener('error', function(event) {
    if (event && event.message && typeof event.message === 'string' && event.message.includes('ResizeObserver')) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }, true);
  
  // 方法4: 重写ResizeObserver.prototype.observe方法
  const originalObserve = window.ResizeObserver?.prototype?.observe;
  if (originalObserve) {
    ResizeObserver.prototype.observe = function(target) {
      try {
        originalObserve.apply(this, arguments);
      } catch (e) {
        // 忽略所有ResizeObserver相关错误
        if (String(e).includes('ResizeObserver')) {
          console.warn('ResizeObserver错误已被抑制');
        } else {
          throw e;
        }
      }
    };
  }
  
  // 方法5: 为Vue的errorHandler增加特殊处理
  if (window.Vue) {
    const originalVueErrorHandler = window.Vue.config.errorHandler;
    window.Vue.config.errorHandler = function(err, vm, info) {
      if (err && String(err).includes('ResizeObserver')) {
        return;
      }
      if (originalVueErrorHandler) {
        originalVueErrorHandler.call(this, err, vm, info);
      }
    };
  }
  
  console.log('ResizeObserver错误处理器已设置，将彻底抑制相关错误');
}

/**
 * 替换全局ResizeObserver
 * 这是一个更激进的方法，添加节流防抖机制
 */
function patchResizeObserver() {
  // 保存原始ResizeObserver实现
  const OriginalResizeObserver = window.ResizeObserver;
  
  if (!OriginalResizeObserver) {
    console.warn('无法找到ResizeObserver实现，跳过补丁应用');
    return;
  }
  
  // 节流函数 - 限制回调的执行频率
  const throttle = (func, limit) => {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  };
  
  // 防抖函数 - 延迟回调执行，重置计时器
  const debounce = (func, delay) => {
    let debounceTimer;
    return function() {
      const context = this;
      const args = arguments;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => func.apply(context, args), delay);
    };
  };
  
  try {
    // 创建增强的ResizeObserver实现 - 使用安全的方式
    window.ResizeObserver = function SafeResizeObserver(callback) {
      // 包装回调函数，添加节流和错误处理
      const safeCallback = (entries, observer) => {
        // 使用setTimeout将回调推到下一个事件循环
        setTimeout(() => {
          try {
            // 特殊处理表格组件
            const hasTableEntries = entries.some(entry => {
              const el = entry.target;
              return el && el.tagName && (
                (el.classList && (
                  el.classList.contains('el-table') || 
                  el.classList.contains('ant-table'))) ||
                el.tagName === 'TABLE' ||
                (el.querySelector && el.querySelector('table'))
              );
            });
            
            // 根据元素类型应用不同级别的节流
            if (hasTableEntries) {
              throttle(() => callback(entries, observer), 150)();
            } else {
              debounce(() => callback(entries, observer), 50)();
            }
          } catch (e) {
            console.warn('ResizeObserver回调执行错误:', e);
          }
        }, 0);
      };
      
      // 使用原始ResizeObserver但传入我们的安全回调
      const instance = new OriginalResizeObserver(safeCallback);
      
      // 返回实例
      return instance;
    };
    
    // 复制原型方法
    window.ResizeObserver.prototype = OriginalResizeObserver.prototype;
    
    console.log('安全版ResizeObserver补丁已应用');
  } catch (e) {
    console.error('应用ResizeObserver补丁时发生错误:', e);
    // 恢复原始实现
    window.ResizeObserver = OriginalResizeObserver;
  }
}

/**
 * 优化Tab切换过渡，减少ResizeObserver错误
 * 通过样式优化减少Tab切换时的布局计算
 */
function optimizeTabTransitions() {
  // 获取所有Tab面板
  const tabPanes = document.querySelectorAll('.el-tab-pane');
  const tables = document.querySelectorAll('.el-table');
  const cards = document.querySelectorAll('.el-card');
  const nestedViews = document.querySelectorAll('.nested-view, .dynamic-content');
  const scrollContainers = document.querySelectorAll('.el-scrollbar, .scrollable-content');
  
  // 应用样式优化
  tabPanes.forEach(pane => {
    if (!pane.classList.contains('is-active')) {
      pane.style.position = 'absolute';
      pane.style.visibility = 'hidden';
      pane.style.zIndex = '-1';
      pane.style.opacity = '0';
      pane.style.pointerEvents = 'none';
      pane.style.transform = 'translateX(-9999px)'; // 移出屏幕减少布局计算
    } else {
      pane.style.position = 'relative';
      pane.style.visibility = 'visible';
      pane.style.zIndex = '1';
      pane.style.opacity = '1';
      pane.style.pointerEvents = 'auto';
      pane.style.transform = 'none';
      
      // 为激活的面板内部的表格应用额外优化
      const paneTables = pane.querySelectorAll('.el-table');
      paneTables.forEach(table => {
        table.style.contain = 'content';
        table.style.transform = 'translateZ(0)';
      });
    }
  });

  // 优化表格渲染
  tables.forEach(table => {
    table.style.contain = 'content';
    table.style.transform = 'translateZ(0)';
    table.style.willChange = 'contents';
    
    // 延迟表格布局计算
    const resizeObserver = table.__vue__?.$el?.__resizeObserver__;
    if (resizeObserver && resizeObserver.disconnect) {
      // 临时断开ResizeObserver
      resizeObserver.disconnect();
      // 100ms后重新连接
      setTimeout(() => resizeObserver.connect(), 100);
    }
  });

  // 优化卡片渲染
  cards.forEach(card => {
    card.style.contain = 'content';
    card.style.transform = 'translateZ(0)';
  });

  // 优化嵌套视图
  nestedViews.forEach(view => {
    view.style.contain = 'content';
    view.style.transform = 'translateZ(0)';
  });
  
  // 优化滚动容器
  scrollContainers.forEach(container => {
    container.style.contain = 'content';
    container.style.transform = 'translateZ(0)';
  });

  // 延迟清除willChange属性以避免长期性能影响
  setTimeout(() => {
    tables.forEach(table => {
      table.style.willChange = 'auto';
    });
  }, 1000);
}

/**
 * 使用IntersectionObserver优化Tab面板的渲染
 * 只有在面板可见时才执行内部元素的布局计算
 */
function setupLazyTabRendering() {
  // 创建IntersectionObserver来监控元素可见性
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const el = entry.target;
      
      // 如果元素可见
      if (entry.isIntersecting) {
        // 移除延迟渲染标志
        el.classList.remove('lazy-render');
        
        // 触发内部元素的布局更新
        const tables = el.querySelectorAll('.el-table');
        tables.forEach(table => {
          if (table.__vue__ && table.__vue__.doLayout) {
            // 如果是Element UI表格，调用其doLayout方法
            setTimeout(() => table.__vue__.doLayout(), 50);
          }
        });
      } else {
        // 如果元素不可见，添加延迟渲染标志
        el.classList.add('lazy-render');
      }
    });
  }, {
    threshold: 0.01  // 只要有1%可见就触发
  });
  
  // 监控所有Tab面板
  document.querySelectorAll('.el-tab-pane').forEach(pane => {
    observer.observe(pane);
  });
  
  // 返回清理函数
  return () => observer.disconnect();
}

/**
 * 设置Tab切换监视器
 * 监听Tab相关class变化，在切换时应用优化
 */
function setupTabChangeMonitor() {
  // 创建MutationObserver以监听class变化
  const observer = new MutationObserver((mutations) => {
    let tabChanged = false;
    
    mutations.forEach(mutation => {
      if (mutation.type === 'attributes' && 
          mutation.attributeName === 'class' &&
          (mutation.target.classList.contains('el-tabs__item') || 
           mutation.target.classList.contains('el-tab-pane'))) {
        tabChanged = true;
      }
    });
    
    if (tabChanged) {
      // Tab发生变化时应用优化
      // 使用防抖和多次执行策略确保适应不同渲染时机
      optimizeTabTransitions();
      
      // 50ms后再次优化，处理延迟渲染的元素
      setTimeout(optimizeTabTransitions, 50);
      
      // 150ms后第三次优化，处理异步加载的内容
      setTimeout(optimizeTabTransitions, 150);
      
      // 500ms后第四次优化，处理可能的AJAX加载内容
      setTimeout(optimizeTabTransitions, 500);
    }
  });
  
  // 开始监听整个文档的class变化
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
    subtree: true
  });
  
  // 返回清理函数
  return () => observer.disconnect();
}

/**
 * 应用全局CSS优化
 * 减少不必要的尺寸计算和布局
 */
function applyGlobalCSSOptimizations() {
  // 创建样式元素
  const style = document.createElement('style');
  style.id = 'resize-observer-optimizations';
  style.textContent = `
    /* 避免不必要的表单元素大小计算 */
    input, select, textarea {
      box-sizing: border-box !important;
    }
    
    /* 抑制表格单元格尺寸变化引起的回流 */
    table, tr, td, th {
      table-layout: fixed !important;
      box-sizing: border-box !important;
    }
    
    /* 优化tabs组件布局计算 */
    .ant-tabs-content, .el-tabs__content {
      overflow: visible !important;
      will-change: auto !important;
    }
    
    /* 优化卡片和面板组件 */
    .ant-card, .el-card {
      contain: content !important;
    }
    
    /* 优化列表和表格组件 */
    .ant-table, .el-table {
      contain: content !important;
    }
    
    /* 统一客户管理页面特殊修复 */
    .unified-customer-list .ant-table,
    .unified-customer-list .card-view,
    .unified-customer-list .map-view {
      contain: content !important;
      transform: translateZ(0);
    }
    
    .unified-customer-list .table-view .ant-table-wrapper,
    .unified-customer-list .table-toolbar {
      contain: content !important;
    }
    
    .unified-customer-form .ant-form-item,
    .unified-customer-detail .detail-card {
      contain: layout style !important;
    }
  `;
  
  // 添加到页面头部
  document.head.appendChild(style);
  console.log('已应用全局CSS优化');
}

/**
 * 修复常见UI库组件
 * 针对Ant Design和Element Plus组件中的ResizeObserver使用
 */
function patchUILibraryComponents() {
  // 在document ready或组件挂载后执行
  const patchComponents = () => {
    // 修复Tabs组件
    document.querySelectorAll('.ant-tabs, .el-tabs').forEach(tabsElement => {
      // 为Tabs组件添加固定高度以减少尺寸计算
      if (!tabsElement.getAttribute('data-patched')) {
        // 标记已修补
        tabsElement.setAttribute('data-patched', 'true');
        
        // 找到内容区域，添加contain属性
        const contentElement = tabsElement.querySelector('.ant-tabs-content, .el-tabs__content');
        if (contentElement) {
          contentElement.style.contain = 'content';
          contentElement.style.overflowX = 'hidden';
        }
      }
    });
    
    // 修复表格组件
    document.querySelectorAll('.ant-table, .el-table').forEach(tableElement => {
      if (!tableElement.getAttribute('data-patched')) {
        tableElement.setAttribute('data-patched', 'true');
        tableElement.style.contain = 'content';
      }
    });
  };
  
  // 立即执行一次
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchComponents);
  } else {
    patchComponents();
  }
  
  // 页面完全加载后再次执行
  window.addEventListener('load', patchComponents);
  
  console.log('已修复UI库组件');
}

/**
 * 设置动态补丁机制
 * 使用MutationObserver监视DOM变化，动态应用补丁
 */
function setupDynamicPatching() {
  // 创建MutationObserver实例
  const observer = new MutationObserver((mutations) => {
    let shouldPatchComponents = false;
    
    // 检查是否需要应用补丁
    for (const mutation of mutations) {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        // 检查添加的节点是否包含我们关心的组件
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node;
            if (element.querySelector && element.querySelector('.ant-tabs, .el-tabs, .ant-table, .el-table')) {
              shouldPatchComponents = true;
              break;
            }
          }
        }
      }
      
      if (shouldPatchComponents) {
        break;
      }
    }
    
    // 如果需要，应用补丁
    if (shouldPatchComponents) {
      patchUILibraryComponents();
    }
  });
  
  // 开始观察整个文档
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  console.log('已设置动态补丁机制');
}

/**
 * 应用完整的ResizeObserver补丁
 * 集成所有修复方法，一次性解决ResizeObserver问题
 */
export function applyResizeObserverPatch() {
  console.log('应用ResizeObserver全面补丁');
  
  // 1. 设置错误处理器
  setupResizeObserverErrorHandler();
  
  // 2. 替换ResizeObserver实现
  patchResizeObserver();
  
  // 3. 应用全局CSS优化，防止不必要的尺寸计算
  applyGlobalCSSOptimizations();
  
  // 4. 修复常见的UI库组件
  patchUILibraryComponents();
  
  // 5. 设置MutationObserver监控DOM变化，动态应用补丁
  setupDynamicPatching();
  
  console.log('ResizeObserver补丁已完全应用');
} 